import { ApiError } from '@reflex/ui/client/http';

export const UNCLAIMABLE_DOMAIN_COPY =
  'Consumer mail providers and public suffixes are not claimable. Enter a domain your company controls.';

/**
 * Toast copy for the typed domain-claim error codes the server returns.
 * Claiming and verifying are open to every tier, so the only entitlement
 * refusal here is the one that answers a single control: turning SSO
 * discovery on without enterprise identity.
 */
const CLAIM_ERROR_COPY: Record<string, { title: string; description: string }> = {
  invalid_domain: {
    title: 'That is not a valid domain',
    description: 'Enter a bare email domain like example.com, without user or protocol parts.',
  },
  unclaimable_domain: {
    title: 'This domain cannot be claimed',
    description: UNCLAIMABLE_DOMAIN_COPY,
  },
  domain_already_claimed: {
    title: 'Another company already owns this domain',
    description:
      'A domain resolves to exactly one company, so it cannot be claimed twice. If it should be yours, contact support.',
  },
  auto_join_organization_not_owned: {
    title: 'That organization is not available',
    description: 'Auto-join can only route to an organization your company owns.',
  },
  domain_claim_verify_throttled: {
    title: 'Checked a moment ago',
    description: 'Give DNS a minute to propagate, then check again.',
  },
  too_many_domain_claims: {
    title: 'Claim limit reached',
    description:
      'Your company holds the maximum number of domain claims. Remove one you no longer need.',
  },
  domain_claim_not_verified: {
    title: 'Verify the domain first',
    description: 'You can turn routing on once the DNS record is verified.',
  },
  enterprise_identity_not_entitled: {
    title: 'SSO discovery needs enterprise identity',
    description:
      'Your plan does not include enterprise identity, so sign-ins for this domain cannot be routed to your own provider. Talk to your Reflex account team to turn it on.',
  },
  domain_claim_platform_approved: {
    title: 'This claim is platform approved',
    description: 'Platform-approved claims are managed by Reflex support, not DNS checks.',
  },
  domain_claim_dns_verified: {
    title: 'DNS already verifies this domain',
    description:
      'The claim is proved by its DNS record and re-checked daily, so there is nothing to verify by hand.',
  },
};

type ClaimErrorCopy = { title: string; description: string };

/**
 * Copy for a typed domain-claim refusal, or `undefined` for anything else.
 * `overrides` lets a surface with a different reader (platform admins
 * acting for a company) replace the customer-facing wording per code.
 */
export function claimErrorCopy(
  err: unknown,
  overrides: Record<string, ClaimErrorCopy> = {},
): ClaimErrorCopy | undefined {
  if (!(err instanceof ApiError) || !err.code) return undefined;
  return overrides[err.code] ?? CLAIM_ERROR_COPY[err.code];
}
